import Vue from 'vue';
import axios from 'axios';
import { apolloClient } from '@/plugins/apollo';
import { RequestFileUploadUrl } from '@/graphql';

const CANCEL_ERROR_CODE = 'ECONNABORTED';

const state = {
  uploads: {},
  errors: {},
};

const actions = {
  async uploadFile(
    context,
    { id, file, reviewId, kind, contentType }
  ) {
    // Register the upload with an abort controller and progress.
    const upload = {
      id,
      file,
      kind,
      reviewId,
      contentType,
      controller: new axios.CancelToken.source(),
      progress: 0,
      complete: false,
    };
    context.commit('registerUpload', upload);

    const fileUri = await context.dispatch(
      'uploadFileAndGetUri',
      upload
    );

    context.commit('setRequestComplete', upload);
    return fileUri;
  },
  async uploadFileAndGetUri(
    context,
    { id, file, reviewId, kind, contentType, controller }
  ) {
    const response = await apolloClient.mutate({
      mutation: RequestFileUploadUrl,
      variables: { reviewId, kind, name: file.name },
    });
    const uploadInfo = response.data.requestFileUploadUrl;
    const uploadUrl = uploadInfo.signedUploadUrl;
    context.commit('setUploadUri', { id, uri: uploadInfo.fileUri });

    try {
      await axios.put(uploadUrl, file, {
        headers: { 'Content-Type': contentType },
        onUploadProgress: (event) =>
          context.commit('updateProgress', {
            id,
            event,
          }),
        cancelToken: controller.token,
      });
    } catch (e) {
      let error = 'Uploading failed, please try again.';
      if (e.message === CANCEL_ERROR_CODE) {
        error = 'Upload cancelled.';
      }
      context.commit('setError', {
        id,
        value: error,
      });
      return null;
    }

    return uploadInfo.fileUri;
  },
  cancelSingleUpload(context, file) {
    context.commit('cancelUpload', file);
    context.commit('clearUploadState', file);
  },
  cancelUploads(context) {
    context.commit('cancelUploads');
    context.commit('clearAllUploadState');
  },
  clearUploadState(context) {
    context.commit('clearAllUploadState');
  },
};

const mutations = {
  registerUpload: (state, upload) => {
    Vue.set(state.uploads, upload.id, upload);
  },
  updateProgress: (state, { id, event }) => {
    const upload = {
      ...state.uploads[id],
      progress: event.loaded / event.total,
    };
    Vue.set(state.uploads, id, upload);
  },
  setUploadUri: (state, { id, uri }) => {
    const upload = {
      ...state.uploads[id],
      uri,
    };
    Vue.set(state.uploads, id, upload);
  },
  cancelUpload: (state, file) => {
    const upload = state.uploads[file?.id];
    if (upload?.controller) {
      upload.controller.cancel(CANCEL_ERROR_CODE);
    }
  },
  cancelUploads: (state) => {
    for (const [, value] of Object.entries(state.uploads)) {
      if (value && value.controller) {
        value.controller.cancel(CANCEL_ERROR_CODE);
      }
    }
  },
  clearUploadState: (state, file) => {
    Vue.delete(state.uploads, file.id);
    Vue.delete(state.errors, file.id);
  },
  clearAllUploadState: (state) => {
    state.uploads = {};
    state.errors = {};
  },
  setRequestComplete: (state, upload) => {
    if (upload.id in state.uploads) {
      const updatedUpload = {
        ...state.uploads[upload.id],
        complete: true,
      };
      Vue.set(state.uploads, upload.id, updatedUpload);
    }
  },
  setError: (state, { id, value }) => {
    Vue.set(state.errors, id, value);
  },
};

const getters = {
  uploads: (state) => state.uploads,
  uploadsInProgress: (state) =>
    Object.values(state.uploads).some((upload) => !upload.complete),
  uploadProgress: (state) =>
    Object.values(state.uploads).reduce(
      (acc, upload) => ({
        ...acc,
        [upload.id]: upload.progress,
      }),
      {}
    ),
  errors: (state) => state.errors,
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
